import type { OnboardingConfig } from './types/onboardingConfig';

/**
 * The published config. Module-scoped mutable state on purpose: the values are process-wide,
 * and the alternative — threading them through React context — cannot serve the readers that
 * need them, which include plain non-React functions (`sendStreamMessage`, the axios
 * interceptors, `getLobPublishableKey`).
 */
let config: OnboardingConfig = {};

/**
 * Publish consumer-supplied config. Called by `OnboardingRoot` synchronously during render —
 * NOT from an effect, which would fire after children commit and let boot-time requests
 * reach the API before the override landed.
 *
 * Merges rather than replaces, and is therefore idempotent under re-renders and StrictMode's
 * double invoke: publishing the same object twice is a no-op, and a caller can layer a
 * partial config over an earlier one.
 */
// A plain shallow merge is correct because `OnboardingConfig` is flat — every member is a
// leaf. It used to carry `transifex: { token }`, `posthog: {…}` and `datadog: {…}`, and each
// nested group needed spreading by hand or a shallow merge would replace it wholesale and
// silently drop a sibling key. The vendor groups left with `vidaVendors`, and `transifex.token`
// flattened to `transifexToken`; keeping the shape flat is what keeps this function trivial.
export const setRuntimeConfig = (next: OnboardingConfig): void => {
  config = { ...config, ...next };
};

/**
 * Test-only reset. Config is process-wide, so a test that publishes one would otherwise leak
 * into every test after it in the same worker.
 */
export const resetRuntimeConfig = (): void => {
  config = {};
};

/**
 * `undefined` unless explicitly set, in either channel. Env vars are strings, so an unset one
 * is `undefined` and an empty one is `''` — the `||` collapses both, which matches how the
 * reads these replaced behaved (`process.env.X || fallback`).
 */
const resolveString = (runtime: string | undefined, env: string | undefined) =>
  runtime || env || undefined;

/**
 * Booleans arrive from env as the literal string `'true'`. A runtime value wins even when it
 * is `false` — hence the `!== undefined` check rather than `??`, which would let an explicit
 * `false` fall through to the env var and re-enable something a consumer just turned off.
 */
const resolveBool = (runtime: boolean | undefined, env: string | undefined): boolean => {
  if (runtime !== undefined) return runtime;
  return env === 'true';
};

/**
 * Thrown when no `apiBaseUrl` resolves, rather than defaulting to staging.
 *
 * This used to fall back to `https://staging-api.vida.com` behind a `console.error`. A
 * `console.error` is not a failure — the app keeps running, against the wrong environment. And
 * a consumer is exactly who hits it: our env fallbacks look for `EXPO_PUBLIC_API_BASE_URL` /
 * `EXPO_PUBLIC_B2B_BASE_URL`, but login and VidaReactNative both name theirs
 * `EXPO_PUBLIC_VIDA_API_URL`, and `EXPO_PUBLIC_*` does not inline inside `node_modules`
 * anyway. So in a consumer the fallback is dead twice over and `config.apiBaseUrl` is the only
 * channel — meaning a production host that forgets it would have shipped onboarding traffic to
 * staging with one console line as the only signal.
 *
 * `applyBaseUrl` is the only caller, so this surfaces at the first request rather than at
 * import. Note it is registered on `authClient` as well as `builtInApiClient`, and the
 * `authClient` path runs inside `performRefresh`, which reports rather than rejects — so that
 * catch checks for an actual 4xx before reporting `refresh-failed`, and logs this error instead
 * of mistaking it for a dead session.
 */
const missingApiBaseUrl = () =>
  new Error(
    '@vidahealth/onboarding: no apiBaseUrl configured. Pass it as ' +
      '<OnboardingRoot config={{ apiBaseUrl }} /> — note EXPO_PUBLIC_* vars do not inline ' +
      'inside node_modules, so the env fallback cannot work in a consuming app.'
  );

/**
 * One resolver per config value: consumer config first, then the matching `EXPO_PUBLIC_*`
 * var, then whatever default that value had before it moved here.
 *
 * The keys are the public API — `getConfig` derives its parameter type from them, so this
 * object is the single place a value is added, and adding one immediately gives every call
 * site autocomplete. Every key is a flat leaf name matching `OnboardingConfig`'s own shape —
 * there are no nested groups left, so no dotted paths and no second naming scheme.
 *
 * ## What is deliberately NOT here
 *
 * The PostHog and Datadog groups used to be, along with an `embedded` boolean that decided
 * whether to honour them. All three are gone: vendor credentials belong to whoever
 * constructs the vendor, so they travel to `createVidaVendors` in
 * `#modules/vidaVendors` instead, and a host that injects its own `analytics` never states
 * them at all. `embedded` existed only to suppress a Datadog init that no longer happens
 * unless someone asks for it. See `docs/vendor-ownership-proposal.md` §3.5.
 *
 * What is left is genuinely "leaf values the flow reads", which is what this module claimed
 * to be all along.
 */
const resolvers = {
  /**
   * Throws when nothing resolves, matching login's `requireString`. Two env names are tried
   * because our own `.env.*` files set `EXPO_PUBLIC_B2B_BASE_URL` while the variable a
   * consumer would expect is `EXPO_PUBLIC_API_BASE_URL`.
   *
   * This deliberately reversed an earlier decision to fall back to staging behind a
   * `console.error`. That reasoning held while the standalone app was the only caller — a
   * rejected request deep in the flow is worse than a warning when the developer can see the
   * console. It stops holding for a consumer: nothing there can satisfy the env fallback (see
   * `missingApiBaseUrl`), so the default became the *normal* path for a host that forgot the
   * config, and "onboarding silently talks to staging in production" is far worse than a loud
   * failure. Throwing costs the standalone app nothing, since its `.env.*` always resolves.
   */
  apiBaseUrl: (): string => {
    const resolved =
      config.apiBaseUrl ||
      process.env.EXPO_PUBLIC_API_BASE_URL ||
      process.env.EXPO_PUBLIC_B2B_BASE_URL;

    if (resolved) return resolved;

    throw missingApiBaseUrl();
  },

  skipLegacySession: (): boolean =>
    resolveBool(config.skipLegacySession, process.env.EXPO_PUBLIC_SKIP_LEGACY_SESSION),

  vidaEnv: (): string | undefined =>
    resolveString(config.vidaEnv, process.env.EXPO_PUBLIC_VIDA_ENV),

  streamChatApiKey: (): string | undefined =>
    resolveString(config.streamChatApiKey, process.env.EXPO_PUBLIC_STREAM_CHAT_API_KEY),

  /**
   * Defaults to `''` rather than `undefined`, preserving `constants/lob.ts`'s shape — its
   * callers treat an empty key as "address verification unavailable".
   */
  lobPublishableKey: (): string =>
    resolveString(config.lobPublishableKey, process.env.EXPO_PUBLIC_LOB_PUBLISHABLE_KEY) ?? '',

  transifexToken: (): string | undefined =>
    resolveString(config.transifexToken, process.env.EXPO_PUBLIC_TRANSIFEX_TOKEN),
};

/** Every readable config value. Autocompletes at each `getConfig` call site. */
export type ConfigKey = keyof typeof resolvers;

/**
 * Read one config value: consumer-supplied if `OnboardingRoot` was given a `config`,
 * otherwise the `EXPO_PUBLIC_*` fallback.
 *
 * Generic over the key so the return type is the individual value's, not a union —
 * `getConfig('skipLegacySession')` is `boolean`, `getConfig('vidaEnv')` is
 * `string | undefined`, and a typo in the key is a compile error rather than a
 * silently-undefined read.
 *
 * **Never call this at module scope.** Config is published during `OnboardingRoot`'s render,
 * so an import-time read sees an empty config and freezes the env fallback — which is
 * `undefined` in a consumer's bundle. See the module docblock for the module-scope reads that
 * had to be undone before these resolvers were usable.
 */
export function getConfig<K extends ConfigKey>(name: K): ReturnType<(typeof resolvers)[K]> {
  // The cast is unavoidable: indexing with a generic `K` widens the call signature to a union
  // of every resolver, so TS resolves the return to that union rather than to this key's. The
  // annotation above is the contract, and each resolver's own return type is what makes it
  // true.
  return resolvers[name]() as ReturnType<(typeof resolvers)[K]>;
}
